import type { ElementType, ReactNode } from 'react';
import { cn } from '@shared/lib/utils';

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  /** Optional leading icon — rendered in a brand-gradient tile for continuity. */
  icon?: ElementType;
  /** Optional breadcrumb or tag rendered next to the title */
  badge?: ReactNode;
  className?: string;
}

/**
 * Page-level header. Relies on spacing for breathing room. An optional `icon`
 * renders a brand-gradient tile that echoes the PageHero, giving list/detail
 * pages a cohesive brand cue without a full gradient banner.
 */
export function PageHeader({
  title,
  description,
  actions,
  icon: Icon,
  badge,
  className,
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-4 pb-2 sm:flex-row sm:items-start sm:justify-between',
        className,
      )}
    >
      <div className="flex items-start gap-3.5">
        {Icon && (
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-sm"
            style={{ background: 'var(--brand-primary)' }}
          >
            <Icon className="h-5 w-5" />
          </span>
        )}
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-bold tracking-tight sm:text-2xl">{title}</h1>
            {badge}
          </div>
          {description && (
            <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
              {description}
            </p>
          )}
        </div>
      </div>

      {actions && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  );
}
